import { noteFromApi } from './obsStorage';

/**
 * Resuming an observation whose debrief is still outstanding.
 *
 * The observation and its notes are already saved; what is missing is the
 * in-memory state the later screens work from. Rebuilding it means loading the
 * observation into context and re-entering at the summary, which regenerates
 * the statements from the saved notes.
 */

/** The list payload → the `obs` shape the observation screens expect. */
export function obsFromListItem(item) {
  return {
    id: item.id,
    school: item.school,
    school_name: item.school_name,
    teacher: item.teacher,
    grade_level: item.grade_level,
    subject: item.subject,
    observation_date: item.observation_date,
    session_code: item.session_code,
    status: item.status,
  };
}

/** The list payload → the `teacher` shape the debrief and email expect. */
export function teacherFromListItem(item) {
  const fullName = item.teacher_name || '';
  return {
    id: item.teacher,
    full_name: fullName,
    display_name: item.teacher_display_name || fullName,
    initials: item.teacher_initials || '',
    first_name: fullName.split(' ')[0] || '',
  };
}

/**
 * Load a pending observation into context and return where to send the user.
 * Re-entering at the summary is deliberate: the statements are rebuilt from
 * the saved notes, so nothing is carried on stale client state.
 */
export function resumeObservation(item, { setObs, setTeacher, resetObs }) {
  resetObs();
  setObs(obsFromListItem(item));
  setTeacher(teacherFromListItem(item));
  return '/observe/conclusions';
}

/** Where an observation picks up again, by how far it got. */
export const RESUME_ROUTE = {
  in_progress:      '/observe/evidence',
  obs_complete:     '/observe/conclusions',
  debrief_pending:  '/observe/conclusions',
  debrief_complete: '/observe/feedback',
  // fully_complete is finished — there is nothing left to do.
};

export const isResumable = (status) => Boolean(RESUME_ROUTE[status]);

/**
 * Pick an observation back up from wherever it stopped.
 *
 * Whatever the later screens need is reloaded from the server rather than
 * assumed: the notes for a half-finished observation, the debrief record for
 * one that has been debriefed but not sent.
 */
export async function resumeFromHistory(item, ctx, api) {
  const {
    setObs, setTeacher, resetObs, setNotesFromServer,
    setFinalBest, setFinalBetter, setNextStep, setCloseOutcome,
  } = ctx;

  const route = RESUME_ROUTE[item.status];
  if (!route) return null;

  resetObs();
  setObs(obsFromListItem(item));
  setTeacher(teacherFromListItem(item));

  // Still being observed, or observed but not yet reviewed — the notes are
  // what the next screen works from.
  if (item.status === 'in_progress' || item.status === 'obs_complete') {
    const { data } = await api.notes(item.id);
    const list = data.results ?? data;
    setNotesFromServer(list.map(noteFromApi).reverse());
  }

  // Debriefed but not sent — the email is built from what was actually said,
  // so that comes back off the debrief record.
  if (item.status === 'debrief_complete') {
    const { data } = await api.debrief(item.id);
    setFinalBest(data.leverage_best || []);
    setFinalBetter(data.leverage_better || []);
    setNextStep(data.agreed_next_step || '');
    setCloseOutcome(data.close_outcome || 'now');
  }

  return route;
}
